import { useEffect, useState, useContext } from 'react';
import { useCookies } from 'react-cookie';
import {
  glassboxEvent,
  isExcludedUserEmail,
  isGlassboxTrackingSuppressed,
  suppressGlassboxTracking,
} from '../helpers/GlassboxTracking';
import UserContext from '../library/UserContext';
import { useLocation } from 'react-router-dom';

/**
 * Glassbox Initializer Component
 * 
 * Sets user identification for Glassbox session recording
 * Glassbox automatically captures all user interactions and page views
 * 
 * User data is fetched from UserContext
 * Email is read from browser cookie 'HomeIDP.previous.subjects'
 */
const GlassboxInitializer = (): JSX.Element | null => {
  const [cookies] = useCookies(['HomeIDP.previous.subjects', 'member_id', 'member_name']);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [lastTrackedContextKey, setLastTrackedContextKey] = useState<string | null>(null);
  const userContext = useContext(UserContext) as any;
  const location = useLocation();

  useEffect(() => {
    // Prevent multiple initializations
    if ((window as any)._glassboxUserSet) return;
    
    let retryCount = 0;
    const MAX_RETRIES = 10; // Stop after 5 seconds (10 * 500ms)
    
    const initializeGlassbox = async () => {
      try {
        // Automated/test accounts are kept out of Glassbox entirely - no user
        // identification and no button clicks for the rest of the session.
        // Checked before the detector wait below so suppression engages even when the
        // Glassbox script has not landed yet.
        if (isExcludedUserEmail(userContext?.userInfo?.email)) {
          suppressGlassboxTracking();
          (window as any)._glassboxUserSet = true;
          return;
        }

        // Wait for Glassbox to be loaded - with retry limit
        if (typeof window._detector === 'undefined') {
          if (retryCount >= MAX_RETRIES) {
            console.warn('[Glassbox Init] Detector not loaded after maximum retries. Glassbox tracking disabled.');
            return;
          }
          retryCount++;
          const timer = setTimeout(() => {
            initializeGlassbox();
          }, 500);
          return () => clearTimeout(timer);
        }

        // Wait for user context to be available
        if (!userContext?.userInfo) {
          return;
        }
        
        // Wait for permissions to be loaded (which includes isInternalUser)
        if (!userContext?.permissionsList || Object.keys(userContext.permissionsList).length === 0) {
          return;
        }

        const userData = userContext.userInfo;
        
        // Get isInternalUser from already loaded permissions (from OneLoginAssociations API at app start)
        let isInternalUser = false;
        const permissions = userContext.permissionsList;
        if (permissions) {
          // Get isInternalUser from any org entry (all orgs have the same value)
          const firstOrgKey = Object.keys(permissions)[0];
          if (firstOrgKey && permissions[firstOrgKey]) {
            isInternalUser = permissions[firstOrgKey].isInternalUser || false;
          }
        }

        // Prepare user identification data
        const userId = userData.msid || '';
        const username = userData.memberName || userData.msid || '';
        const module_name = 'FrontDoor';
        const roleType = userData.roleType || '';
        const isExternal = isInternalUser === true ? false : true;
        const userEmail =  userData.email;
        
        const identificationData = {
          userId: String(userId),
          username: String(username),
          userEmail: String(userEmail),
          module_name: String(module_name),
          roleType: String(roleType),
          isExternal: String(isExternal),
          sessionStart: new Date().toISOString()
        };
        
        // Mark as initialized
        (window as any)._glassboxUserSet = true;
        setUserDataLoaded(true);
        
        // Send user identification as a custom event
        // Delay ensures Glassbox is fully ready
        setTimeout(() => {
          try {
            glassboxEvent('user_identification', identificationData);
          } catch (error) {
            console.error('[Glassbox Init] Error sending user identification:', error);
          }
        }, 1000);
        
      } catch (error) {
        console.error('[Glassbox Init] Error initializing Glassbox:', error);
      }
    };

    initializeGlassbox();
  }, [userContext?.userInfo, userContext?.permissionsList]);

  // Track member context changes for Advanced Reporting
  useEffect(() => {
    // Only track if Glassbox is initialized
    if (!(window as any)._glassboxUserSet) return;

    // Excluded accounts mark themselves initialized to stop the retry loop, so the
    // suppression flag - not _glassboxUserSet - is what keeps them untracked here.
    if (isGlassboxTrackingSuppressed()) return;

    const memberId = cookies.member_id;
    const memberName = cookies.member_name;
    
    // Check if we're on a reports page
    const pathname = location.pathname || window.location.pathname;
    const isReportsPage =
      pathname.includes('/reports') ||
      pathname.includes('/adhoc-reports') ||
      pathname.includes('/my-reports') ||
      pathname.includes('/shared-reports');
    const contextKey = `${memberId}-${pathname}`;
    
    if (isReportsPage && memberId && memberName && contextKey !== lastTrackedContextKey) {
      // Get user data for additional context
      const userData = userContext?.userInfo;
      const permissions = userContext?.permissionsList;
      
      // Get isInternalUser from permissions
      let isInternalUser = false;
      if (permissions && permissions[memberId]) {
        isInternalUser = permissions[memberId].isInternalUser || false;
      }
      
      // Send event for Advanced Reporting access with member context
      const reportingContextData = {
        userId: userData?.email || '',
        userEmail: userData?.email || '',
        memberName: memberName,
        memberId: memberId,
        roleType: userData?.roleType || '',
        isExternal: String(isInternalUser === true ? false : true),
        accessedAt: new Date().toISOString(),
        module_name: 'Advanced Reporting'
      };
      
      glassboxEvent('advanced_reporting_member_context', reportingContextData);
      
      // Update last tracked context
      setLastTrackedContextKey(contextKey);
    }
  }, [cookies.member_id, cookies.member_name, lastTrackedContextKey, location.pathname, userContext]);

  return null;
};

export default GlassboxInitializer;